<template>
    <b-modal
        id="rule-group-search-modal"
        ref="modal"
        :title="$t('규칙 그룹 선택')"
        size="xl"
        body-class="search-modal"
        hide-footer
        @hidden="resetModal"
        @hide="resetModal"
    >
        <b-container fluid>
            <b-card no-body bg-variant="white" class="mb-3">
                <template #header>
                    <div class="d-flex align-items-center justify-content-between">
                        <h5 class="mb-0">{{ $t('규칙 그룹 검색하기') }} </h5>
                        <div v-show="listLoading">
                            <div class="d-flex align-items-center">
                                <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                                <p class="d-inline-block mb-0">{{ $t('조회중') }}...</p>
                            </div>
                        </div>
                        <div v-show="!listLoading">
                            <b-btn
                                variant="primary"
                                :disabled="listLoading"
                                @click="searchData"
                            >
                                {{ $t('검색하기') }}
                            </b-btn>
                        </div>
                    </div>
                </template>

                <div class="p-3">
                    <b-row>
                        <b-col cols="12">
                            <b-form-group
                                id="fieldset-input-rule-group-name"
                                :label="$t('검색어')"
                                label-for="rule-group-input-rule-group-name"
                                class="mb-0"
                            >
                                <b-form-input id="rule-group-input-rule-group-name"
                                              v-model="searchFormValue.search"
                                              :disabled="listLoading"
                                              trim
                                              @keypress.enter="searchData"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>
            </b-card>

            <b-card
                :class="[{ 'shadow-sm': searchResults || (!listLoading && searchResults) }, 'list-container', 'column-wrapped']"
                :bg-variant="!searchResults ? 'light' : 'white'"
                no-body
            >
                <template #header>
                    <div class="d-flex align-items-center justify-content-between" style="height: 36px">
                        <h6 class="mb-0">{{ $t('규칙 그룹 목록') }}</h6>
                        <div class="d-flex align-items-center">
                            <b-button variant="outline-secondary" class="mr-2"
                                      @click="resetSelected"
                            >
                                {{ $t('선택 초기화') }}
                            </b-button>
                            <b-button variant="outline-primary" @click="selectData">
                                {{ $t('선택한 규칙 그룹 추가') }}
                            </b-button>
                        </div>
                    </div>
                </template>
                <div class="table-container">
                    <b-table
                        fixed responsive
                        :items="searchResults"
                        :fields="tableColumns"
                        class="mb-0"
                        show-empty
                        :per-page="pagination.perPage"
                        :current-page="pagination.currentPage"
                        :busy="listLoading"
                    >
                        <template #empty>
                            <PageEmptyComponent
                                :text="$t('조회된 규칙 그룹이 없습니다.')"
                                :loading="listLoading"
                                :loadingText="$t('규칙 그룹 목록을 조회 중입니다.')"
                            />
                        </template>

                        <template v-if="type==='checkbox'" #head(selected)>
                            <b-form-checkbox
                                v-model="selectedAll"
                                :indeterminate="indeterminate"
                                :disabled="!(searchResults?.length > 0)"
                                aria-label="Select all rows"
                                @change="toggleSelectAll"
                            ></b-form-checkbox>
                        </template>
                        <template #cell(selected)="{ item }">
                            <b-form-checkbox v-if="type === 'checkbox' && searchResults?.length > 0"
                                             v-model="mapSelected[item.id].selected"
                                             aria-label="Select rows"
                                             @change="selectRow(item)"
                            ></b-form-checkbox>
                            <b-form-radio
                                v-if="type==='radio'"
                                v-model="selectedId"
                                :value="item.id"
                            />
                        </template>

                        <template #empty>
                            <PageEmptyComponent
                                :text="$t('조회된 규칙 그룹이 없습니다.')"
                                :loading="listLoading"
                                :loadingText="$t('규칙 그룹 목록을 조회 중입니다.')"
                            />
                        </template>
                        <template #cell(name)="row">
                            <span v-b-tooltip.hover="get_tooltip_title(row.value)">{{ row.value }}</span>
                        </template>

                        <template #cell(key)="row">
                            <span v-b-tooltip.hover="get_tooltip_title(row.value)">{{ row.value }}</span>
                        </template>

                        <template #cell(operator)="row">
                            <span v-b-tooltip.hover="get_tooltip_title(row.value)">{{ row.value }}</span>
                        </template>

                        <template #cell(conditions)="row">
                            {{ row.value.length }}개
                        </template>

                        <template #cell(children)="row">
                            {{ row.value.length }}개
                        </template>

                        <template #cell(detail)="data">
                            <b-btn size="sm"
                                   variant="outline-primary"
                                   @click="openDetailModal('detail', data.item)">상세
                            </b-btn>
                        </template>

                        <template #cell(updated_at)="row">
                            {{ get_date(row.value) }}

                        </template>
                        <template #cell(created_at)="row">
                            {{ get_date(row.value) }}
                        </template>
                    </b-table>
                    <div v-if="searchResults && searchResults.length > 0" class="pagination-container">
                        <b-pagination
                            v-model="pagination.currentPage"
                            :total-rows="pagination.totalRows"
                            :per-page="pagination.perPage"
                            align="fill"
                            size="sm"
                        ></b-pagination>
                    </div>
                </div>
            </b-card>
        </b-container>
        <RuleGroupModal ref="ruleGroupModal"
                        :detail-id="detailId"
        />
    </b-modal>
</template>

<script>
import {listRuleGroups} from '@shared_utils/api/ruleEngine';
import {
    getDate,
    getDefaultPagination,
    onTooltipShow
} from '@shared/components/admin/pages/Management/common-data';
import {columns} from '@shared/components/admin/pages/Management/CampaignConnectRule/RuleGroup/data';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import RuleGroupModal from '@shared/components/admin/pages/Management/CampaignConnectRule/RuleGroup/RuleGroupModal.vue';

export default {
    name: 'RuleGroupSearchModal',
    components: {RuleGroupModal, PageEmptyComponent},
    props: {
        type: {
            type: String,
            default: 'radio', // 'radio' | 'checkbox'
        },
        selectedData: {
            type: Array,
            default: () => [],
        }
    },
    data() {
        return {
            listLoading: false,
            searchResults: null,
            searchFormValue: {
                search: null
            },
            pagination: getDefaultPagination(),

            fillPropsData: false,
            selectedAll: false,
            currentSelectedNum: 0,
            mapSelected: {},
            selectedId: null,
            detailId: null,
        };
    },
    computed: {
        tableColumns() {
            return [
                {key: 'selected', label: '', thStyle: {width: '40px'}, thClass: 'text-center', tdClass: 'text-center'},
                ...columns.map(column => ({...column, label: this.$t(column.label)}))
            ];
        },
        indeterminate() {
            return this.currentSelectedNum > 0 && this.currentSelectedNum < (this.searchResults?.length || 0);
        },
        get_tooltip_title() {
            return (data) => {
                return data ? {title: data} : null;
            };
        },
        get_date() {
            return (date) => {
                return getDate(date);
            };
        }
    },
    mounted() {
        this.$root.$on('bv::tooltip::show', this.onTooltipShow);
        this.$root.$on('bv::modal::show', this.onShowSearchModal);
    },
    beforeDestroy() {
        this.$root.$off('bv::tooltip::show', this.onTooltipShow);
        this.$root.$off('bv::modal::show', this.onShowSearchModal);
    },
    methods: {
        onTooltipShow,
        onShowSearchModal(bvEvent, modalId) {
            if (modalId !== 'rule-group-search-modal') return;
            this.resetModal();
            this.searchData();
        },
        async searchData() {
            try {
                this.listLoading = true;
                this.searchResults = null;
                this.currentSelectedNum = 0;

                const res = await listRuleGroups({
                    params: {
                        ...this.searchFormValue,
                        page: this.pagination.currentPage,
                        per_page: this.pagination.perPage,
                    },
                });

                if (res.data == null || res.data.length === 0) {
                    this.$EventBus.$emit('toast-success', this.$t('검색 완료'), this.$t('규칙 그룹 검색 결과가 없습니다.'));
                    this.searchResults = [];
                    return;
                }

                const {rule_groups, total, current_page} = res.data;

                this.searchResults = rule_groups.map((item, idx) => {
                    const data = {
                        ...item,
                        index: idx
                    };
                    if (this.selectedData?.length > 0) {
                        data.selected = this.selectedData.includes(item.id);
                        if (this.type === 'radio' && data.selected) {
                            this.selectedId = item.id;
                        }
                    }
                    if (this.mapSelected[item.id] == null) {
                        this.$set(this.mapSelected, item.id, data);
                    }
                    return data;
                });
                Object.values(this.mapSelected).forEach(item => {
                    if (item.selected) {
                        this.currentSelectedNum += 1;
                    }
                });
                this.pagination.totalRows = total;
            } catch (error) {
                console.error(error);
                this.$EventBus.$emit('toast-failure', this.$t('검색 오류'), this.$t('규칙 그룹 검색 중 오류가 발생했습니다. 잠시후 재시도 해주세요.'));
            } finally {
                this.listLoading = false;
            }
        },
        async openDetailModal(mode, item) {
            this.detailId = item.id;
            this.$refs.ruleGroupModal.changeMode(mode);
            this.$bvModal.show('rule-group-detail-modal');
        },
        selectRow(row) {
            if (row.selected) {
                this.currentSelectedNum += 1;
            } else {
                this.currentSelectedNum -= 1;
            }
        },
        toggleSelectAll(value) {
            if (this.searchResults?.length <= 0) return;

            for (let i = 0; i < this.searchResults.length; i++) {
                const elem = this.searchResults[i];
                this.mapSelected[elem.id].selected = !!value;
            }

            let selectedCount = 0;
            Object.values(this.mapSelected).forEach((item) => {
                if (item.selected) {
                    selectedCount += 1;
                }
            });
            this.currentSelectedNum = selectedCount;
        },
        resetSelected() {
            if (this.type === 'radio') {
                this.selectedId = null;
                return;
            }

            this.selectedAll = false;
            this.currentSelectedNum = 0;
            Object.entries(this.mapSelected).forEach(([key, value]) => {
                this.mapSelected[key].selected = false;
            });
        },
        selectData() {
            if (this.type === 'radio') {
                if (this.selectedId == null) {
                    this.$EventBus.$emit('toast-failure', this.$t('규칙 그룹 선택'), this.$t('규칙 그룹을 선택해주세요.'));
                    return;
                }
                this.$emit('on-select', {[this.selectedId]: this.mapSelected[this.selectedId]});
                this.$bvModal.hide('rule-group-search-modal');
                return;
            }

            let selectedNum = 0;
            const mapSelectedItem = {};
            Object.values(this.mapSelected).forEach((item) => {
                if (item.selected) {
                    selectedNum += 1;
                    mapSelectedItem[item.id] = item;
                }
            });
            if (selectedNum === 0) {
                this.$EventBus.$emit('toast-failure', this.$t('규칙 그룹 선택'), this.$t('규칙 그룹을 선택해주세요.'));
                return;
            }
            this.$emit('on-select', mapSelectedItem);
            this.$bvModal.hide('rule-group-search-modal');
        },
        resetModal() {
            this.listLoading = false;
            this.searchResults = null;
            this.searchFormValue = {
                search: null,
            };
            this.pagination = getDefaultPagination();
            this.selectedId = null;

            this.selectedAll = false;
            this.currentSelectedNum = 0;
            this.mapSelected = {};
            this.selectedId = null;
            this.detailId = null;
        },
    }
};
</script>
